/****************************************************
 * Module Name:  UploadToRapnet.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/

import React, { useEffect, useState } from "react";
import { Box, Button, Container, Tooltip, alpha, styled, Checkbox, Typography, Badge, Stack } from "@mui/material";
import { DataGridPro, GridEditInputCell, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import CircularProgress from "@mui/material/CircularProgress";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import Select from "react-select";
import Constants from "../../utility/constants";
import CommonStoneSelection from "../common/commonStoneSelection";
import Utility from "../../utility/utility";
import StoneDetailModal from "../common/stoneDetailModal";
import CommonSnackbar from "../common/commonSnackBar";
import StoneProxy from "../../utility/proxy/manager/stoneProxy";
import renderCellExpand from "../common/dataGridCellExpand";
import UploadToRapnetModal from "./uploadToRapnetModal";
import RapnetUploadProxy from "../../utility/proxy/manager/rapnetUploadProxy";
import EditIcon from '@mui/icons-material/Edit';
import Papa from 'papaparse';
import { BackupTable } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import TabMenuSnackbar from "../common/tabMenuSnackbar";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import colourchart from '../../images/ColorScheme.png'
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import RapnetLoginsProxy from "../../utility/proxy/manager/rapnetLoginsProxy";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import KapanHistoryModal from "../manufacturing/operations/kapanHistoryModal";
import PacketHistoryModal from "../common/packetHistoryModal";
import PageTitle from "../common/pageTitle";

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    "& .super-app-theme--AVAILABLE": {
        color: "#000",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            color: "#000",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--HOLD": {
        backgroundColor: "#B6B9D6",
        color: "black",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#B6B9D6",
            color: "black",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    "& .super-app-theme--MEMO_OUT": {
        backgroundColor: "#CAF7F6",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        "&:hover": {
            backgroundColor: "#CAF7F6",
            fontSize: Constants.defaultTableFontSize,
            fontWeight: Constants.defaultFontWeight,
        },
    },
    '& .MuiDataGrid-columnHeader': {
        color: '#fff',
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        backgroundColor: 'grey',
    },
    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
        py: "8px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },
    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
        py: "15px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },
    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
        py: "22px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },

}));

const StyledMenu = styled((props) => (
    <Menu
        elevation={0}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
        }}
        {...props}
    />
))(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 6,
        marginTop: theme.spacing(1),
        minWidth: 180,
        color:
            theme.palette.mode === 'light'
                ? 'rgb(55, 65, 81)'
                : theme.palette.grey[300],
        boxShadow:
            'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        '& .MuiMenu-list': {
            padding: '4px 0',
        },
        '& .MuiMenuItem-root': {
            '& .MuiSvgIcon-root': {
                fontSize: 18,
                color: theme.palette.text.secondary,
                marginRight: theme.spacing(1.5),
            },
            '&:active': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    theme.palette.action.selectedOpacity
                ),
            },
        },
    },
}));

export default function UploadToRapnet() {
    const { apiRef, handleCellClick } = Utility.useSingleClickEdit();
    const [stoneData, setStoneData] = useState([]);
    const [fetchData, setFetchData] = useState(true);
    const [stoneLoader, setStoneLoader] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [selectionModel, setSelectionModel] = useState([]);
    const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
    const [stoneId, setStoneId] = useState("");
    const [discountSet, setDiscountSet] = useState({});
    const [ppcDiscountSet, setppcDiscountSet] = useState({});
    const [successMessage, setSuccessMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [loadingButton, setLoadingButton] = useState(false);
    const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
    const [showUploadToRapnetModal, setShowUploadToRapnetModal] = useState(false);
    const [columnNames, setColumnNames] = React.useState([]);
    const local_Permission = Constants.USER_PERMISSION.filter((iPermission) =>
        iPermission.permissionid === Constants.PERMISSIONS.OPERATION_RAPNET_UPLOAD);

    const [uploadSuccessSnackbar, setUploadSuccessSnackbar] = useState(false);
    const [data, setData] = useState([]);
    const [uploadLoader, setUploadLoader] = useState(false);
    const [stoneDataMap, setStoneDataMap] = useState({});
    const [rapnetStockUpload, setRapnetStockUpload] = useState([]);
    const [kapanId, setKapanId] = useState("");
    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);
    const [packetId, setPacketId] = useState("");
    const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);

    //top most summary table 
    const [noOfSelectedStones, setNoOfSelectedStones] = useState("0");
    const [selectedTotalSize, setSelectedTotalSize] = useState("0");
    const [selectedAvgDiscount, setSelectedAvgDiscount] = useState("0");
    const [selectedavgPrice, setSelectedavgPrice] = useState("0");
    const [selectedtotalPrice, setSelectedtotalPrice] = useState("0");
    const [rapnetData, setRapnetData] = useState({
        rapnetSelected: [],
        rapnetIdOptions: [],
    });

    const [uploadedStonesCheck, setUploadedStonesCheck] = useState(false);
    const [isUploadedStonesChecked, setIsUploadedStonesChecked] = useState(false);
    // Color Scheme Drop Down Button
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleCloseButton = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        PageTitle("OPERATION_RAPNET_UPLOAD");
    })

    useEffect(() => {
        let stockMap = new Map();
        if (stoneData.length !== 0) {
            stoneData.forEach(iData => {
                stockMap.set(iData.stoneid, iData);
            })
            setStoneDataMap(stockMap);
        }
    }, [stoneData]);

    const handleSearch = () => {
        setSelectionModel([]);
        setStoneLoader(true);
        let data = {
            "status": ["AVAILABLE", "MEMO_OUT", "HOLD", "OFFLINE"],
            "israpnetstock": true
        };
        StoneProxy.GetFilteredStock(data, (responseData) => {
            //Success
            if (responseData) {
                responseData = responseData.map(res => {
                    res.editedrapnetdiscount = res.discount;
                    res.editedrapnetppc = res.price;
                    res.editedrapnettotal = res.total;
                    return res;
                })
                prepareColumns();
                setStoneData(responseData);
                loadTotalData(responseData);
                setStoneLoader(false);
                setFetchData(false);
            } else {
                // Failure
                setStoneLoader(false);
                setFetchData(false);
                setErrorSnackbar(true);
            }
        });
    };

    const fullOptionToValue = (selectedData) => {
        let newArr = [];
        selectedData.map((data) => {
            if (data.value === "*") {
                return rapnetData.rapnetIdOptions.map((option) => {
                    if (option.value !== "*") {
                        newArr.push(option.value);
                        return true;
                    }
                    return true;
                });
            }
            return true;
        })
        return Utility.GetValueArrayWithStarReplace(selectedData, newArr);
    };

    const handleUploadedStoneData = () => {
        setSelectionModel([]);
        setStoneLoader(true);

        let rapnetids = fullOptionToValue(rapnetData.rapnetSelected)
        RapnetUploadProxy.GetUploadedRapnetStones(rapnetids, (responseData) => {
            //Success
            if (responseData) {
                prepareColumns();
                setStoneData(responseData);
                loadTotalData(responseData);
                setStoneLoader(false);
                setFetchData(false);
            } else {
                // Failure
                setStoneLoader(false);
                setFetchData(false);
                setErrorSnackbar(true);
            }
        });
    };

    useEffect(() => {
        if (uploadedStonesCheck && rapnetData.rapnetIdOptions.length === 0) {
            fetchRapnetLoginsData();
        } else if (!uploadedStonesCheck) {
            handleSearch();
        }
    }, [uploadedStonesCheck]);

    const prepareColumns = () => {
        let columnNames = [];

        Constants.USER_COLUMN_PERMISSION.forEach(iColPermission => {
            if (columnsData.hasOwnProperty(iColPermission)) {
                const columnData = columnsData[iColPermission];
                columnNames.push(columnData);
                if (columnData.field === "stoneid") {
                    columnNames.push({
                        field: "editeddiscount",
                        headerName: "DIFF DIS",
                        headerAlign: "center",
                        align: "right",
                        width: 80,
                        type: "number",
                        valueFormatter: (params) => params.value,
                        editable: (params) => {
                            return Utility.isAddAllowed(local_Permission) && params.row.colortype === "WHITE";
                        },
                        renderCell: (params) => (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ paddingRight: '18px !important' }}>{params.value}</span>
                                {Utility.isAddAllowed(local_Permission) && params.row.colortype === "WHITE" && <EditIcon style={{ marginLeft: 10, marginBottom: 3, fontSize: '1rem', }} />}
                            </div>
                        ),
                        renderEditCell: (params) => {
                            return (
                                Utility.isAddAllowed(local_Permission) && params.row.colortype === "WHITE" ?
                                    <Box onChange={(e) => {
                                        setDiscountSet(prevDiscountSet => ({
                                            ...prevDiscountSet,
                                            [params.row.stoneid]: e.target.value,
                                        }));
                                    }}><GridEditInputCell {...params} /></Box>
                                    : null
                            )
                        },
                        cellClassName: (params) => {
                            return (Utility.isAddAllowed(local_Permission) && params.row.colortype === "WHITE") ? 'lavenderCell' : 'cursorNA';
                        }
                    });
                    columnNames.push({
                        field: "ppcdiscount",
                        headerName: "DIFF PPC",
                        headerAlign: "center",
                        width: 80,
                        align: "right",
                        type: "number",
                        valueFormatter: (params) => params.value,
                        editable: (params) => {

                            return Utility.isAddAllowed(local_Permission) &&
                                params.row.colortype === "FANCY";
                        },
                        renderCell: (params) => (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{ paddingRight: '18px !important' }}>{params.value}</span>
                                {Utility.isAddAllowed(local_Permission) && params.row.colortype === "FANCY" && <EditIcon style={{ fontSize: '1rem', marginLeft: 10, marginBottom: 3, }} />}
                            </div>
                        ),
                        renderEditCell: (params) => {
                            return (
                                Utility.isAddAllowed(local_Permission) &&
                                    params.row.colortype === "FANCY" ?
                                    <Box onChange={(e) => {
                                        setppcDiscountSet(prevppcDiscountSet => ({
                                            ...prevppcDiscountSet,
                                            [params.row.stoneid]: e.target.value,
                                        }));
                                    }}><GridEditInputCell {...params} /></Box>
                                    : null
                            )
                        },
                        cellClassName: (params) => {
                            return (Utility.isAddAllowed(local_Permission) && params.row.colortype === "FANCY") ? 'lavenderCell' : 'cursorNA';
                        }
                    });
                } else if (columnData.field === "discount") {
                    columnNames.push({
                        field: "editedrapnetdiscount",
                        headerName: "RAP DIS",
                        headerAlign: "center",
                        align: "right",
                        width: 75,
                        type: "number",
                        valueFormatter: (params) => params.value,
                        renderCell: (params) => (<span style={{ fontWeight: "bold" }}>{Utility.ConvertNumberWithSpecifiedDigits(params.row.editedrapnetdiscount)}</span>),
                    });
                } else if (columnData.field === "price") {
                    columnNames.push({
                        field: "editedrapnetppc",
                        headerName: "RAP PPC",
                        headerAlign: "center",
                        align: "right",
                        width: 85,
                        type: "number",
                        valueFormatter: (params) => params.value,
                        renderCell: (params) => (<span style={{ fontWeight: "bold" }}>{Utility.ConvertNumberWithSpecifiedDigits(params.row.editedrapnetppc)}</span>),
                    });
                } else if (columnData.field === "total") {
                    columnNames.push({
                        field: "editedrapnettotal",
                        headerName: "RAP TOTAL",
                        headerAlign: "center",
                        align: "right",
                        width: 85,
                        type: "number",
                        valueFormatter: (params) => params.value,
                        renderCell: (params) => (<span style={{ fontWeight: "bold" }}>{Utility.ConvertNumberWithSpecifiedDigits(params.row.editedrapnettotal)}</span>),
                    });
                }
            }
        });
        setColumnNames(columnNames);
    }


    const loadTotalData = (data) => {
        let totalSize = 0;
        let totalPrice = 0;   //ppc*size
        let total = 0;       //rapnetprice*size

        data.forEach((iData) => {
            let pricePerCarat = iData.price
            totalSize += iData.size;
            totalPrice += (iData.size * pricePerCarat);
            total += (iData.size * ((100 * pricePerCarat) / (100 + iData.discount)));

        });
        setNoOfSelectedStones(data.length);
        setSelectedTotalSize(Utility.ConvertNumberWithSpecifiedDigits(totalSize));
        setSelectedtotalPrice(Utility.ConvertNumberWithSpecifiedDigits(totalPrice));
        setSelectedavgPrice(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    totalPrice / totalSize
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );
        setSelectedAvgDiscount(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    (100 - (totalPrice / total) * 100) * -1
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );
    };

    useEffect(() => {
        if (fetchData) {
            handleSearch();
        }
    }, [fetchData]);

    useEffect(() => {
        if (selectionModel.length <= 0) {
            loadTotalData(stoneData);
            return;
        }
        let totalSize = 0;
        let totalPrice = 0;   //ppc*size
        let total = 0;       //rapnetprice*size
        stoneData.forEach((iData) => {
            if (selectionModel.includes(iData.stoneid)) {
                let pricePerCarat = iData.price
                totalSize += iData.size;
                totalPrice += (iData.size * pricePerCarat);
                total += (iData.size * ((100 * pricePerCarat) / (100 + iData.discount)));
            }
        });
        setNoOfSelectedStones(selectionModel.length);
        setSelectedTotalSize(Utility.ConvertNumberWithSpecifiedDigits(totalSize));
        setSelectedtotalPrice(Utility.ConvertNumberWithSpecifiedDigits(totalPrice));
        setSelectedavgPrice(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    totalPrice / totalSize
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );
        setSelectedAvgDiscount(
            totalPrice > 0
                ? Utility.ConvertNumberWithSpecifiedDigits(
                    (100 - (totalPrice / total) * 100) * -1
                )
                : Utility.ConvertNumberWithSpecifiedDigits(0)
        );

    }, [selectionModel]);

    const fetchRapnetLoginsData = () => {
        RapnetLoginsProxy.getAllRapnetLoginsMini((res) => {
            if (res) setAllRapnetLoginsData(res);
        })
    }

    const setAllRapnetLoginsData = (res) => {
        if (res) {
            let optionsArray = [];
            optionsArray.push({ value: "*", label: "-- ANY --" });
            res.map((data) => {
                let option = { value: data, label: data }
                optionsArray.push(option);

                return true;
            })
            setRapnetData({
                rapnetIdOptions: optionsArray,
                rapnetSelected: optionsArray.length > 0 ? [optionsArray[0]] : []
            })
        }
    }

    const handleSelection = (data) => {
        const [tempStockData, tempSelectionModel] = Utility.handleSelection(data, stoneData, selectionModel);

        setStoneData(tempStockData);
        setSelectionModel(tempSelectionModel);
    }

    const handleClose = () => {
        setShowStoneDetailModal(false);
        setShowSuccessSnackbar(false);
        setUploadSuccessSnackbar(false)
    };

    const handleStoneDetailModal = (params) => {
        setStoneId(params.value);
        setShowStoneDetailModal(true);
    };

    const uploadToRapnet = (data) => {
        setLoadingButton(true);
        RapnetUploadProxy.createRapnetStone((data),
            (res) => {
                if (res) {
                    //Success
                    setShowSuccessSnackbar(true);
                    setSuccessMessage("Stone Uploaded To Rapnet Successfully");
                    setSnackbarVariant("success");
                    setLoadingButton(false);
                } else {
                    //Failure
                    setShowSuccessSnackbar(true);
                    setSuccessMessage(Constants.APiFailErrorMessage);
                    setSnackbarVariant("error");
                    setLoadingButton(false);
                }
                setShowUploadToRapnetModal(false);
                setDiscountSet({});
                setppcDiscountSet({});
                handleSearch();
            })
    }

    const handleValidation = () => {
        //TODO do validation if needed here
        return true;
    };

    const handleClear = () => {
        const fileInput = document.getElementById('file-input');
        fileInput.value = null;
    }

    const handleUpload = (file) => {
        const reader = new FileReader();
        setUploadLoader(true);

        reader.onload = () => {
            const text = reader.result;
            Papa.parse(text, {
                header: true,
                skipEmptyLines: true,
                complete: (result) => {
                    let ids = []
                    let headersFromCSV = Object.keys(result.data[0]);
                    let requiredColumns = ['stoneid', 'editeddiscount/ppcdiscount'];
                    let matchingColumns = headersFromCSV.filter(field => requiredColumns.includes(field));
                    if (matchingColumns.length === requiredColumns.length) {
                        let updatedStoneData = [...stoneData];
                        result.data.forEach(record => {
                            updatedStoneData.forEach(row => {
                                if (row.stoneid === parseInt(record.stoneid)) {
                                    if (row.colortype === "WHITE") {
                                        if (!(record['editeddiscount/ppcdiscount'] === "" || isNaN(parseInt(record['editeddiscount/ppcdiscount'])))) {
                                            row.editeddiscount = parseInt(record['editeddiscount/ppcdiscount']) || row.editeddiscount;
                                            row.editedrapnetdiscount = row.editeddiscount + row.discount;
                                            row.editedrapnetppc = row.rapnetprice + (row.rapnetprice * (row.editedrapnetdiscount / 100));
                                            row.editedrapnettotal = row.editedrapnetppc * row.size;
                                        }
                                        setDiscountSet(prevDiscountSet => ({
                                            ...prevDiscountSet,
                                            [row.stoneid]: (record['editeddiscount/ppcdiscount'] === "" || isNaN(parseInt(record['editeddiscount/ppcdiscount']))) ? (row.discount).toString() : record['editeddiscount/ppcdiscount']
                                        }));
                                    }
                                    else {
                                        if (!(record['editeddiscount/ppcdiscount'] === "" || isNaN(parseInt(record['editeddiscount/ppcdiscount'])))) {
                                            row.ppcdiscount = parseInt(record['editeddiscount/ppcdiscount']) || row.ppcdiscount;
                                            row.editedrapnetppc = row.ppcdiscount;
                                            row.editedrapnetdiscount = 100 - ((row.editedrapnetppc / row.rapnetprice) * 100);
                                            row.editedrapnettotal = row.editedrapnetppc * row.size;
                                        }
                                        setppcDiscountSet(prevppcDiscountSet => ({
                                            ...prevppcDiscountSet,
                                            [row.stoneid]: (record['editeddiscount/ppcdiscount'] === "" || isNaN(parseInt(record['editeddiscount/ppcdiscount']))) ? (row.price).toString() : record['editeddiscount/ppcdiscount']
                                        }));
                                    }
                                    ids = result.data.map(record => parseInt(record.stoneid));
                                    setSelectionModel(ids);
                                }
                            });
                        });
                        // Separate stones to update and remaining stones
                        let stonesToUpdate = updatedStoneData.filter(row => ids.includes(row.stoneid));
                        let remainingStones = updatedStoneData.filter(row => !ids.includes(row.stoneid));

                        // Concatenate stonesToUpdate at the beginning
                        updatedStoneData = [...stonesToUpdate, ...remainingStones];
                        setData(ids);
                        setStoneData(updatedStoneData);
                        setUploadLoader(false);
                        setUploadSuccessSnackbar(true);
                    } else {
                        console.error('CSV columns do not match table columns.');
                        setData([]);
                        setUploadLoader(false);
                        setUploadSuccessSnackbar(true);
                    }
                },
                error: (error) => {
                    console.error('Error parsing CSV:', error);
                    setData([]);
                    setUploadLoader(false);
                }
            });
        };
        reader.readAsText(file);
    };

    const processRowUpdate = (newRow, oldRow) => {
        const changedFeilds = Utility.getChangedFields(newRow, oldRow);
        if (changedFeilds.length > 0) {
            const { id, field, value } = changedFeilds[0];
            if (value) {
                const updatedStoneData = stoneData.map(row => {
                    if (row.stoneid === id) {
                        let updatedRow = { ...row };
                        if (field === "editeddiscount") {
                            updatedRow.editeddiscount = parseInt(value) || row.editeddiscount;
                            updatedRow.editedrapnetdiscount = updatedRow.editeddiscount + row.discount;
                            updatedRow.editedrapnetppc = row.rapnetprice + (row.rapnetprice * (updatedRow.editedrapnetdiscount / 100));
                            updatedRow.editedrapnettotal = updatedRow.editedrapnetppc * row.size;
                            setDiscountSet(prevDiscountSet => ({
                                ...prevDiscountSet,
                                [row.stoneid]: value
                            }));
                        }
                        else {
                            updatedRow.ppcdiscount = parseInt(value) || row.ppcdiscount;
                            updatedRow.editedrapnetppc = updatedRow.ppcdiscount;
                            updatedRow.editedrapnetdiscount = ((updatedRow.editedrapnetppc / row.rapnetprice) * 100) - 100;
                            updatedRow.editedrapnettotal = updatedRow.editedrapnetppc * row.size;
                            setppcDiscountSet(prevppcDiscountSet => ({
                                ...prevppcDiscountSet,
                                [row.stoneid]: value
                            }));
                        }
                        return updatedRow;
                    }
                    return row;
                });
                setStoneData(updatedStoneData);
                const updatedRow = updatedStoneData.find(row => row.stoneid === id);
                return updatedRow;
            }
            else {
                return { ...oldRow };
            }
        } else {
            // No changes detected, return the newRow to trigger exit edit mode
            return { ...newRow };
        }
    }


    const handleProcessRowUpdateError = (error) => {
        // console.error(error);
    };

    const columnsData = {
        "5dd43313-18b9-41a5-9e07-09d61ad6863c": {
            field: "stoneid",
            headerName: "STONEID",
            width: 80,
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.NeedToShowStoneDetailModal() ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handleStoneDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
        },
        "9dfe5dd6-726c-465b-9f61-22fca6660b4f": {
            field: "kapanid",
            headerName: "KAPANID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (Utility.NeedToShowKapanDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid);
                        setShowKapanDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            width: 80,
        },
        "8a492e00-6167-45de-b078-e572e47c8a39": {
            field: "packetid",
            headerName: "PACKETID",
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (Utility.NeedToShowPacketDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid)
                        setPacketId(params.row.packetid)
                        setShowPacketDetailModal(true)
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            width: 80,
        },
        "52599510-9698-4cd3-90b7-7ac22b6af7e2": {
            field: "stonetype",
            headerName: "STONETYPE",
            headerAlign: "center",
            align: "center",
            width: 100,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.stonetype.map(option => option.fullname),
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => Constants.STONE_TYPE[params.value],
            renderCell: (params) => Constants.STONE_TYPE[params.value],
        },
        "62c37a29-d2ff-4c72-8cb3-07e52a83d484": {
            field: "status",
            headerName: "STATUS",
            headerAlign: "center",
            align: "center",
            width: 140,
            type: "singleSelect",
            valueOptions: ["AVAILABLE", "MEMO_OUT", "HOLD"],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
            cellClassName: (params) => `super-app-theme--${params.row.status.toUpperCase()}`,
        },
        "39c634f1-2644-4307-acbb-983b4de4229e": {
            field: "price",
            headerName: "PPC",
            headerAlign: "center",
            align: "right",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.price)
        },
        "9f0b7352-8ca1-459d-944c-eb46dad5aed1": {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            align: "right",
            width: 40,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.size)
        },
        "7df28e0e-0303-4ff8-a432-9dedb384afa8": {
            field: "total",
            headerName: "TOTAL",
            headerAlign: "center",
            align: "right",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.total)
        },
        "1035d1d0-bc7c-404f-8f58-c80bea2de613": {
            field: "clarity",
            headerName: "CLARITY",
            headerAlign: "center",
            align: "center",
            width: 75,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.clarity.map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "fe7c00f1-774a-45a2-aec0-e5fb2ea1101e": {
            field: "color",
            headerName: "COLOR",
            headerAlign: "center",
            align: "center",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.color.map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 65,
            renderCell: renderCellExpand,
        },
        "3597803f-8932-40c6-8de6-81006f4d6d5b": {
            field: "cut",
            headerName: "CUT",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.cut.map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "01e90aca-bdcb-4e8e-80ec-2d7007df8cd3": {
            field: "createdat",
            headerName: "CREATED AT",
            headerAlign: "center",
            align: "center",
            width: 170,
            type: "dateTime",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
        },
        "8296e627-0be5-4a66-830f-ea6867cea137": {
            field: "flo",
            headerName: "FLO",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.flo.map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "daffdd4f-4726-4abc-b8b0-fd4750c874be": {
            field: "lab",
            headerName: "LAB",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.lab.map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "19ce7030-402c-495c-84ac-a29093bdb0ac": {
            field: "locationid",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "ab3e506e-bddb-456d-b967-922dcee74d56": {
            field: "location",
            headerName: "LOCATION",
            headerAlign: "center",
            align: "left",
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.location.map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            width: 130,
            renderCell: (params) => params.row.stoneoldstatus === null ? params.value : (
                <Box
                    sx={{
                        "& .MuiBadge-badge": {
                            height: "0.75rem !important",
                            padding: "2px 4px !important",
                            fontSize: "8px",
                            top: "-1px",
                            right: "-20px",
                            backgroundColor: "#E5D9F2 !important",
                            color: "#624E88 !important",
                            fontWeight: "700"
                        },
                    }}
                >
                    <Badge
                        badgeContent={"TRANSIT"}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        {params.value}
                    </Badge>
                </Box>
            ),
        },
        "f97a0118-3891-4f77-abf7-8636c26d594c": {
            field: "country",
            headerName: "COUNTRY",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.country.map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "859c71c9-c9c8-4f2c-9eb7-08e388e505ad": {
            field: "pol",
            headerName: "POL",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.pol.map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "cb7e6add-67ba-4194-ac49-fd0d967db4e8": {
            field: "quality",
            headerName: "QUALITY",
            headerAlign: "center",
            align: "center",
            width: 80,
            renderCell: renderCellExpand,
        },
        "b718f242-4a82-42e7-b98a-40bbf0f67c21": {
            field: "shade",
            headerName: "SHADE",
            headerAlign: "center",
            align: "center",
            width: 60,
            renderCell: renderCellExpand,
        },
        "eb5db767-47d2-4444-9b52-7dc35c853fc7": {
            field: "shape",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.shape.map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "5541c182-21b8-496e-b342-b0b89f3d302f": {
            field: "sym",
            headerName: "SYM",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "singleSelect",
            valueOptions: Constants.uniqueFilterValues.sym.map(option => option.value),
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        "53c8a138-3f8e-486c-a9ba-f5c671065ad7": {
            field: "rapnetprice",
            headerName: "RAP",
            headerAlign: "center",
            align: "right",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.rapnetprice)
        },
        "798a8663-0f0c-41be-8512-16dd60d5f25e": {
            field: "discount",
            headerName: "DIS",
            headerAlign: "center",
            align: "center",
            width: 50,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.row.discount),
        },
        "567202e6-c31c-4c19-9998-1cacae05af6e": {
            field: "depth",
            headerName: "DEPTH",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "3c62ef48-9538-4df7-9484-a9ae1ba31086": {
            field: "height",
            headerName: "HEIGHT",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "eed9b7eb-17e4-4ec6-9c9d-c299f885544c": {
            field: "width",
            headerName: "WIDTH",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "8eaa8a03-d163-436d-8912-51ea06306f44": {
            field: "certificateid",
            headerName: "CERT",
            headerAlign: "center",
            align: "center",
            width: 100,
            renderCell: renderCellExpand,
        },
        "706a5cfb-e196-43d1-bbe3-c52aad9a46c1": {
            field: "comment",
            headerName: "COMMENT",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "e7210f4a-6e0a-4591-8428-e6845004ed04": {
            field: "crownheight",
            headerName: "CR.HEIGHT",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "c4e65984-1682-4c1e-812b-8938e9078b2d": {
            field: "crownangle",
            headerName: "CR.ANG",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        "0f1de38d-3ae3-4209-a4d5-afe9f3018f22": {
            field: "culetsize",
            headerName: "CULET SZ",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 90,
            renderCell: renderCellExpand,
        },
        "e3f77521-2c57-46bc-a02b-3c884685d2be": {
            field: "culetcondition",
            headerName: "CULET",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "e5c1f34e-6e1c-4ee8-bb8e-3236716fd1ff": {
            field: "depthpercent",
            headerName: "DEPTH%",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 75,
            renderCell: renderCellExpand,
        },
        "e83c2978-9088-49f0-9dc1-bcced73025fd": {
            field: "gridlemin",
            headerName: "GIRDLEMIN",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "cee57bac-9c19-40d5-b51a-51ed913dbf73": {
            field: "intensitylongtitle",
            headerName: "INTENSITYLONGTITLE",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "06d2a589-0d42-4dde-8724-acf2448c839a": {
            field: "laserinscription",
            headerName: "INS",
            headerAlign: "center",
            align: "center",
            width: 40,
            renderCell: renderCellExpand,
        },
        "13d48508-b8c9-4661-8a6c-9fb22a3408a3": {
            field: "measurements",
            headerName: "MEASUREMENTS",
            headerAlign: "center",
            align: "center",
            width: 140,
            renderCell: renderCellExpand,
        },
        "dd934505-1865-48cb-878f-f5d3c18af2d5": {
            field: "note",
            headerName: "NOTE",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "61f8a5f2-8516-419a-b6fd-39ef109cc2f4": {
            field: "paviliondepth",
            headerName: "PV.DEPTH",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "70498a64-1f5f-4f03-8910-a6d7851e4aba": {
            field: "tablepercent",
            headerName: "TABLE%",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 75,
            renderCell: renderCellExpand,
        },
        "db078198-4416-4d53-b3d0-70d3a5418f65": {
            field: "open",
            headerName: "OPEN",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "e9028170-e978-4978-a2c9-1889c1821495": {
            field: "gridlecondition",
            headerName: "GIRDLE COND",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "fe8f55f3-358a-4418-bcdf-c3da4b01db67": {
            field: "gridlepercent",
            headerName: "GIRDLE%",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "daf69bba-bb1f-4d54-9dc7-9383ad429585": {
            field: "pavilionangle",
            headerName: "PV.ANGLE",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "ecfdfc0c-6a25-4a6d-9c8f-3406c23410af": {
            field: "white",
            headerName: "WHITE",
            headerAlign: "center",
            align: "center",
            minWidth: 120,
            renderCell: renderCellExpand,
        },
        "e32fe363-07cc-4307-82d2-82ae1069b5be": {
            field: "age",
            headerName: "AGE",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            valueGetter: (params) => {
                if (params.value)
                    return Utility.ConvertDateToAge(params.value);
                else
                    return null;
            },
            renderCell: (params) => {
                return params.value;
            },
        }
    };

    const rows = stoneData;
    const columns = columnNames;

    function CustomToolbar({ isUploadedStonesChecked, setIsUploadedStonesChecked }) {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    },
                }}
            >
                <CommonStoneSelection
                    title={"Enter multiple Stone Ids"}
                    handleSelection={handleSelection}
                    buttonDisabled={rows.length === 0}
                />

                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Checkbox
                        checked={isUploadedStonesChecked}
                        onChange={(e) => {
                            setIsUploadedStonesChecked(e.target.checked);
                            setUploadedStonesCheck((prevState) => !prevState)
                        }}
                        disabled={stoneLoader}
                        size="small"
                    />
                    <Typography
                        sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: stoneLoader ? "grey" : "black"
                        }}
                    >
                        Uploaded Stones
                    </Typography>
                </Box>
                <Box sx={{ flexGrow: 1, display: { md: "none", lg: "block" } }} />

                {stoneLoader ? (
                    <CircularProgress size="1rem" />
                ) : (
                    <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                            onClick={() => {
                                if (uploadedStonesCheck) {
                                    fetchRapnetLoginsData();
                                } else {
                                    handleSearch();
                                }
                            }}
                            color="primary"
                            fontSize="medium"
                            sx={{ cursor: "pointer" }}
                        />
                    </Tooltip>
                )}

                {Utility.isAddAllowed(local_Permission) ? (
                    <Tooltip title="Upload Stones To Rapnet">
                        <Button
                            variant="outlined"
                            size="small"
                            component="label"
                            sx={{ ml: "5px" }}
                            startIcon={<DiamondOutlinedIcon size="large" />}
                            color="success"
                            disabled={selectionModel.length === 0 || stoneLoader}
                            onClick={() => {
                                let arr = [];
                                selectionModel.map(stoneid => {
                                    let iData = stoneDataMap.get(stoneid);
                                    arr.push(iData);
                                    return true;
                                });
                                setRapnetStockUpload(arr);
                                setShowUploadToRapnetModal(true)
                            }}
                        >
                            Upload To Rapnet
                        </Button>
                    </Tooltip>
                ) : null}

                <Tooltip title="Download Sample Rapnet Operation Upload">
                    <a
                        href="/demo_excel/SampleRapnetOperationUpload.csv"
                        download="SampleRapnetOperationUpload.csv"
                        style={{ color: "red", marginRight: "10px", marginLeft: "10px" }}
                    >
                        <BackupTable size={25} />
                    </a>
                </Tooltip>
                <Tooltip title="Upload Rapnet Operation">
                    <LoadingButton
                        color="info"
                        variant="outlined"
                        size="small"
                        sx={{ mr: "10px" }}
                        component="label"
                        loading={uploadLoader}
                        loadingPosition="start"
                        startIcon={<UploadFileIcon size="large" />}
                        type="submit"
                        disabled={stoneLoader}
                    >
                        Upload
                        <input
                            id="file-input"
                            hidden
                            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                            multiple
                            type="file"
                            onChange={(e) => handleUpload(e.target.files[0])}
                            onClick={(e) => handleClear()}
                        />
                    </LoadingButton>
                </Tooltip>

                {showUploadToRapnetModal ? (
                    <UploadToRapnetModal
                        show={true}
                        loading={loadingButton}
                        stoneData={rapnetStockUpload}
                        stoneIds={selectionModel}
                        discount={discountSet}
                        handleSave={uploadToRapnet}
                        ppcDiscount={ppcDiscountSet}
                        setShow={setShowUploadToRapnetModal}
                        handleValidation={handleValidation}
                    />
                ) : null}
                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        )
    }

    return (
        <Container maxWidth="xxl">
            {data ? (
                <TabMenuSnackbar
                    show={uploadSuccessSnackbar}
                    message={
                        data.length !== 0
                            ? `File loaded Successfully`
                            : `The headers in the CSV file do not match the expected headers or Stone ids do not match `
                    }
                    variant={data.length !== 0 ? "success" : "error"}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={data.length !== 0 ? "Success" : "Error"}
                    handleClose={handleClose}
                />
            ) : null}
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />
            {showKapanDetailModal ? (
                <KapanHistoryModal
                    show={showKapanDetailModal}
                    kapanId={kapanId}
                    close={() => setShowKapanDetailModal(false)}
                />
            ) : null}
            {showPacketDetailModal ? (
                <PacketHistoryModal
                    show={showPacketDetailModal}
                    packetId={packetId}
                    kapanId={kapanId}
                    close={() => setShowPacketDetailModal(false)}
                />
            ) : null}
            <Box display={"flex"} margin={"4px 8px"} justifyContent={"space-between"}>
                <Box
                    sx={{
                        display: "flex",
                        width: "30%",
                        gap: "12px",
                        alignItems: "center"
                    }}
                >
                    <Box sx={{ alignSelf: "center", width: "6rem" }}>
                        <Typography>Rapnet ID</Typography>
                    </Box>

                    <Box
                        sx={{
                            alignSelf: "center",
                            width: "90%",
                            maxWidth: "90%",
                            minWidth: "60%"
                        }}
                    >
                        <Select menuPlacement="auto"
                            blurInputOnSelect={false}
                            className="basic-single"
                            classNamePrefix="select"
                            isClearable={true}
                            isMulti
                            getOptionLabel={(option) => option.label}
                            getOptionValue={(option) => option.value}
                            value={rapnetData.rapnetSelected}
                            isSearchable={true}
                            name="Rapnet ID"
                            isDisabled={!uploadedStonesCheck}
                            menuPortalTarget={document.body}
                            styles={Constants.Styles}
                            options={rapnetData.rapnetIdOptions}
                            onChange={(selectedOption) => {
                                setRapnetData(prevState => ({
                                    ...prevState,
                                    rapnetSelected: selectedOption,
                                }))

                            }
                            }

                        />
                    </Box>
                    <Box
                        sx={{
                            "& .MuiButton-root": {
                                lineHeight: "0 !important",
                                padding: '6px 13px !important',
                            }
                        }}
                    >
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => {
                                handleUploadedStoneData();
                            }}
                            startIcon={<SearchOutlinedIcon size="large" />}
                            sx={{ alignSelf: "center", ml: 2 }}
                            disabled={!uploadedStonesCheck || rapnetData.rapnetSelected.length === 0}                        >
                            Search
                        </Button>
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        flexGrow: 1,
                        flexBasis: 0,
                        overflow: "hidden",
                        color: "black",
                        fontWeight: "normal",
                        px: 1,
                        textAlign: "center",
                        paddingTop: "21px",
                        paddingBottom: "21px",
                        paddingLeft: "235px",
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={2}
                        useFlexGap
                        flexWrap="wrap"
                        sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            justifyContent: "flex-start",
                        }}
                    >
                        <Typography
                            component="span"
                            sx={{ fontSize: Constants.defaultTableFontSize }}
                        >
                            No of Stones: <b>{noOfSelectedStones}</b>
                        </Typography>
                        <Typography
                            component="span"
                            sx={{ fontSize: Constants.defaultTableFontSize }}
                        >
                            Total Weight: <b>{selectedTotalSize}</b>
                        </Typography>
                        <Typography
                            component="span"
                            sx={{ fontSize: Constants.defaultTableFontSize }}
                        >
                            Total Price: <b>{selectedtotalPrice}</b>
                        </Typography>
                        <Typography
                            component="span"
                            sx={{ fontSize: Constants.defaultTableFontSize }}
                        >
                            Avg Price: <b>{selectedavgPrice}</b>
                        </Typography>
                        <Typography
                            component="span"
                            sx={{ fontSize: Constants.defaultTableFontSize }}
                        >
                            Avg Discount: <b>{selectedAvgDiscount}</b>
                        </Typography>
                    </Stack>
                </Box>
                <div>
                    <Box
                        id="demo-customized-button"
                        aria-controls={open ? 'demo-customized-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? 'true' : undefined}
                        variant="outlined"
                        disableElevation
                        onClick={handleClick}
                        endIcon={<KeyboardArrowDownIcon />}
                        sx={{
                            color: '#454545',
                            borderColor: '#454545',
                            borderRadius: "4px",
                            border: "1px solid black",
                            padding: "5px 0 5px 5px",
                            cursor: "pointer",
                            marginTop: "10px"
                        }}
                    >
                        <img
                            src={colourchart}
                            alt="Color Scheme"
                            style={{
                                height: '24px',
                                width: '24px',
                            }}
                        />
                        <KeyboardArrowDownIcon />
                    </Box>
                    <StyledMenu
                        id="demo-customized-menu"
                        MenuListProps={{
                            'aria-labelledby': 'demo-customized-button',
                            style: {
                                maxHeight: 250
                            },
                        }}
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleCloseButton}
                    >
                        <MenuItem onClick={handleCloseButton} disableRipple>
                            <span style={{ height: "15px", width: "15px", backgroundColor: "#000", borderRadius: 20, margin: 10 }}></span>Available
                        </MenuItem>
                        <MenuItem onClick={handleCloseButton} disableRipple>
                            <span style={{ height: "15px", width: "15px", backgroundColor: "#b6b9d6", borderRadius: 20, margin: 10 }}></span>Hold
                        </MenuItem>
                        <MenuItem onClick={handleCloseButton} disableRipple>
                            <span style={{ height: "15px", width: "15px", backgroundColor: "#CAF7F6", borderRadius: 20, margin: 10 }}></span>Memo Out
                        </MenuItem>
                        <MenuItem onClick={handleCloseButton} disableRipple>
                            <span style={{ height: "15px", width: "15px", backgroundColor: "#FEBD7D", borderRadius: 20, margin: 10 }}></span>AVAILABLE (IN_TRANSIT)
                        </MenuItem>
                    </StyledMenu>
                </div>
            </Box>
            {showSuccessSnackbar ? (
                <CommonSnackbar
                    show={true}
                    message={successMessage}
                    variant={snackbarVariant}
                    autoHide={Constants.SnackBarCloseTimer}
                    title={snackbarVariant === "success" ? "Success" : "Failed"}
                    handleClose={handleClose}
                />
            ) : null}
            {showStoneDetailModal ? (
                <StoneDetailModal
                    show={showStoneDetailModal}
                    stoneId={stoneId}
                    handleClose={handleClose}
                />
            ) : null}

            <Box
                sx={{
                    backgroundColor: '#fff',
                    color: '#000',
                    width: "100%",
                    position: "relative",
                    "& .MuiTablePagination-selectLabel": {
                        marginTop: "15px",
                    },
                    "& .MuiTablePagination-displayedRows": {
                        marginTop: "15px",
                    },
                    "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                        fontWeight: Constants.defaultFontWeight,
                    },
                    "& .css-cc8tf1": {
                        fontWeight: Constants.defaultFontWeight,
                    },
                    "& .lavenderCell": {
                        color: "#080808",
                        backgroundColor: "#D8BFD8",
                    },
                    "& .greenCell": {
                        color: "#080808",
                        backgroundColor: "#B6D7A8"
                    },
                    "& .yellowCell": {
                        color: "#080808",
                        backgroundColor: "#F1E68C",
                    },
                    "& .cursorNA": {
                        cursor: "not-allowed"
                    },
                    height: '71vh'
                }}
            >
                <StyledDataGrid
                    disableRowSelectionOnClick={true}
                    localeText={{ noRowsLabel: "No Record Found" }}
                    slots={{
                        toolbar: CustomToolbar,
                    }}
                    rows={rows}
                    density="compact"
                    columns={columns}
                    getRowId={(row) => row.stoneid}
                    checkboxSelection={Utility.isAddAllowed(local_Permission) ? true : false}
                    onRowSelectionModelChange={(newSelectionModel) => {
                        setSelectionModel(newSelectionModel);
                    }}
                    loading={stoneLoader}
                    rowSelectionModel={selectionModel}
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={handleProcessRowUpdateError}
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                // Hide columns id, the other columns will remain visible
                                locationid: false,
                                kapanid: false,
                                packetid: false,
                            },
                        },
                        pagination: {
                            paginationModel: Constants.pageSize,
                        },
                    }}
                    pageSizeOptions={Constants.pageSizeOptions}
                    pagination
                    slotProps={{
                        toolbar: {
                            isUploadedStonesChecked, setIsUploadedStonesChecked
                        },
                    }}
                    apiRef={apiRef}
                    onCellClick={handleCellClick}
                    editMode="cell"
                />
            </Box>
        </Container>
    )
}